import { ref, computed } from 'vue'
import {
  collection,
  query,
  where,
  orderBy,
  limit,
  getDocs,
  doc,
  documentId,
  getDoc,
  onSnapshot,
  Timestamp
} from 'firebase/firestore'
import { getFunctions, httpsCallable } from 'firebase/functions'
import { FirebaseProvider } from '../infrastructure/providers/FirebaseProvider'
import { useCompanyStore } from '../stores/company'
import { simpleCache } from '../utils/simpleCache'
import type { 
  EmailComplete,
  EmailFilters,
  SuggestedAction
} from '../types/email'
import { useEmailAuthStore } from '../stores/auth_email'
import { useFirestoreEmailStore } from '../stores/firestoreEmail'
import { EmailSearchRepository } from '../infrastructure/repositories/EmailSearchRepository'
import type { EmailSearchRequest } from '../types/emailSearch'
import { resolveUserFolder, type FolderDocFields } from '../utils/emailFolders'

// DEBUG: Set to true to bypass email auth and show all company emails
const DEBUG_SKIP_EMAIL_AUTH = false;

export function useEmails() {
  const db = FirebaseProvider.getInstance().getFirestore()
  const functions = getFunctions()
  const companyStore = useCompanyStore()


  // ============================================================================
  // ESTADO
  // ============================================================================

  const emails = ref<EmailComplete[]>([])
  const isLoading = ref(false)
  const error = ref<string | null>(null)
  const totalItems = ref(0)
  const currentPage = ref(1)
  const pageSize = ref(25)

  const totalPages = computed(() => Math.ceil(totalItems.value / pageSize.value))
  
  // Computed properties
  const matchedEmails = computed(() => 
    emails.value.filter(e => e.hasMatch)
  )

  const pendingEmails = computed(() => 
    emails.value.filter(e => e.processingStatus === 'pending')
  )

  const processedEmails = computed(() => 
    emails.value.filter(e => e.processingStatus === 'processed')
  )

  // ============================================================================
  // HELPERS
  // ============================================================================

  /**
   * Convierte datos de Firestore a EmailComplete normalizado
   */
  /** Un correo borrado desde Kila queda en Firestore para poder recuperarlo, pero no debe listarse. */
  const isTrashed = (email: EmailComplete) => (email as any).folder === 'trash'

  const normalizeEmail = (emailDoc: any): EmailComplete => {
    const data = emailDoc.data()
    
    // Buscar matchedShipmentId, matchedPoId o matchedPurchaseOrders en raíz del documento
    const matchedPurchaseOrders = Array.isArray(data.matchedPurchaseOrders) ? data.matchedPurchaseOrders : undefined
    const matchedPurchaseOrderIds = Array.isArray(data.matchedPurchaseOrderIds)
      ? data.matchedPurchaseOrderIds
      : matchedPurchaseOrders?.map((po: any) => po.id).filter(Boolean) || undefined
    const hasMatch = !!(
      data.matchedShipmentId || 
      data.matchedPoId ||
      data.parsed?.references?.matchedPoId ||
      (matchedPurchaseOrders && matchedPurchaseOrders.length > 0)
    )

    const pendingActions = data.parsed?.suggestedActions?.filter(
      (a: SuggestedAction) => a.status === 'pending'
    ) || []

    const appliedActions = data.parsed?.suggestedActions?.filter(
      (a: SuggestedAction) => a.status === 'applied'
    ) || []

    const normalizedAttachments = (data.attachments || []).map((att: any) => ({
      ...att,
      id: att.id || att.filename,
      name: att.filename || att.name || 'archivo',
      contentType: att.contentType || 'application/octet-stream',
      size: att.size || 0,
    }))

    const receivedDate = data.receivedAt instanceof Timestamp ? data.receivedAt.toDate() : new Date(data.receivedAt)

    return {
      id: emailDoc.id,
      ...data,
      from: {
        email: data.fromAddress || '',
        name: data.fromName || data.fromAddress || '',
      },
      to: (data.toAddresses || []).map((addr: string) => ({ email: addr, name: addr })),
      cc: (data.ccAddresses || []).map((addr: string) => ({ email: addr, name: addr })),
      subject: data.subject || '(Sin asunto)',
      body: {
        content: data.bodyHtml || data.bodyText || '',
        contentType: data.bodyHtml ? 'html' : 'text',
      },
      bodyPreview: data.bodyText
        ? data.bodyText.substring(0, 200).replace(/\s+/g, ' ').trim()
        : '',
      hasAttachments: data.hasAttachments || (data.attachments?.length > 0) || false,
      attachments: normalizedAttachments,
      receivedDateTime: receivedDate.toISOString(),
      // Convertir Timestamps a Date
      receivedAt: receivedDate,
      createdAt: data.createdAt instanceof Timestamp ? data.createdAt.toDate() : new Date(data.createdAt),
      updatedAt: data.updatedAt instanceof Timestamp ? data.updatedAt.toDate() : new Date(data.updatedAt),
      // Convertir fechas en parsed.dates si existen
      parsed: data.parsed ? {
        ...data.parsed,
        dates: data.parsed.dates ? {
          etaReported: data.parsed.dates.etaReported ? new Date(data.parsed.dates.etaReported) : undefined,
          etdReported: data.parsed.dates.etdReported ? new Date(data.parsed.dates.etdReported) : undefined,
          ataReported: data.parsed.dates.ataReported ? new Date(data.parsed.dates.ataReported) : undefined,
          atdReported: data.parsed.dates.atdReported ? new Date(data.parsed.dates.atdReported) : undefined,
        } : undefined,
      } : undefined,
      // Campos de matching
      matchedPurchaseOrders,
      matchedPurchaseOrderIds,
      // Campos computados
      hasMatch,
      isProcessed: data.processingStatus === 'processed',
      hasActions: (data.parsed?.suggestedActions?.length || 0) > 0,
      pendingActionsCount: pendingActions.length,
      appliedActionsCount: appliedActions.length
    } as EmailComplete
  }

  // ============================================================================
  // MÉTODOS PRINCIPALES
  // ============================================================================

  /**
   * Obtiene todos los correos con filtros opcionales
   */
  const fetchEmails = async (filters: EmailFilters = {}, force = false) => {
    try {
      isLoading.value = true
      error.value = null

      if (!companyStore.selectedCompanyId) {
        console.error('❌ No hay companyId seleccionado')
        error.value = 'No hay compañía seleccionada'
        isLoading.value = false
        return
      }

      const emailAuthStore = useEmailAuthStore();
      const userEmail = emailAuthStore.user?.user.email;

      if (!DEBUG_SKIP_EMAIL_AUTH && !userEmail) {
        console.error('❌ No hay usuario autenticado')
        error.value = 'No hay usuario autenticado'
        isLoading.value = false
        return
      }

      const cacheKey = `emails_${companyStore.selectedCompanyId}_${userEmail ?? 'debug'}_${JSON.stringify(filters)}`

      // Verificar caché (si no es force)
      if (!force) {
        const cachedData = simpleCache.get(cacheKey)
        if (cachedData) {
          emails.value = cachedData.items
          totalItems.value = cachedData.total
          isLoading.value = false
          return
        }
      }


      const emailsRef = collection(db, 'Companies', companyStore.selectedCompanyId, 'emails')

      let emailsResults: EmailComplete[]

      if (DEBUG_SKIP_EMAIL_AUTH || !userEmail) {
        // Modo debug: sin filtro de usuario
        let emailsQuery = query(
          emailsRef,
          orderBy('receivedAt', 'desc'),
          limit(100)
        )

        if (filters.processingStatus && filters.processingStatus.length === 1) {
          emailsQuery = query(
            emailsRef,
            where('processingStatus', '==', filters.processingStatus[0]),
            orderBy('receivedAt', 'desc'),
            limit(100)
          )
        }

        const snapshot = await getDocs(emailsQuery)
        emailsResults = snapshot.docs.map(emailDoc => normalizeEmail(emailDoc))
      } else {
        // Dos queries en paralelo: syncedFromEmail OR toAddresses
        let syncedQuery = query(
          emailsRef,
          where('syncedFromEmail', '==', userEmail),
          orderBy('receivedAt', 'desc'),
          limit(100)
        )
        let toQuery = query(
          emailsRef,
          where('toAddresses', 'array-contains', userEmail),
          orderBy('receivedAt', 'desc'),
          limit(100)
        )

        if (filters.processingStatus && filters.processingStatus.length === 1) {
          syncedQuery = query(
            emailsRef,
            where('syncedFromEmail', '==', userEmail),
            where('processingStatus', '==', filters.processingStatus[0]),
            orderBy('receivedAt', 'desc'),
            limit(100)
          )
          toQuery = query(
            emailsRef,
            where('toAddresses', 'array-contains', userEmail),
            where('processingStatus', '==', filters.processingStatus[0]),
            orderBy('receivedAt', 'desc'),
            limit(100)
          )
        }

        const [syncedSnapshot, toSnapshot] = await Promise.all([
          getDocs(syncedQuery),
          getDocs(toQuery)
        ])

        // Merge y deduplicar por ID
        const mergedMap = new Map<string, EmailComplete>()
        syncedSnapshot.docs.forEach(d => mergedMap.set(d.id, normalizeEmail(d)))
        toSnapshot.docs.forEach(d => {
          if (!mergedMap.has(d.id)) mergedMap.set(d.id, normalizeEmail(d))
        })

        emailsResults = [...mergedMap.values()]
          .sort((a, b) => {
            const timeA = a.receivedAt instanceof Timestamp ? a.receivedAt.toMillis() : new Date(a.receivedAt || 0).getTime()
            const timeB = b.receivedAt instanceof Timestamp ? b.receivedAt.toMillis() : new Date(b.receivedAt || 0).getTime()
            return timeB - timeA
          })
          .slice(0, 50)
      }

      totalItems.value = emailsResults.length
      emails.value = emailsResults

      // Guardar en caché (5 minutos)
      simpleCache.set(
        cacheKey, 
        { items: emailsResults, total: totalItems.value }, 
        5 * 60 * 1000
      )


    } catch (err) {
      error.value = err instanceof Error ? err.message : 'Error cargando correos'
      console.error('❌ [useEmails] Error:', err)
    } finally {
      isLoading.value = false
    }
  }

  /**
   * Obtiene un correo específico por ID
   */
  const fetchEmailById = async (emailId: string): Promise<EmailComplete | null> => {
    try {
      if (!companyStore.selectedCompanyId) {
        console.error('❌ No hay companyId seleccionado')
        return null
      }

      const emailRef = doc(
        db,
        'Companies',
        companyStore.selectedCompanyId,
        'emails',
        emailId
      )
      const emailSnap = await getDoc(emailRef)

      if (!emailSnap.exists()) {
        console.warn(`❌ Email no encontrado: ${emailId}`)
        return null
      }

      return normalizeEmail(emailSnap)
    } catch (err) {
      console.error('❌ Error obteniendo correo por ID:', err)
      return null
    }
  }

  /**
   * Convierte receivedAt (Date | Timestamp) a milisegundos para ordenar.
   */
  const toMillis = (date: any): number => {
    if (!date) return 0
    if (date instanceof Date) return date.getTime()
    if (typeof date.toDate === 'function') return date.toDate().getTime()
    if (typeof date === 'string' || typeof date === 'number') return new Date(date).getTime()
    return 0
  }

  /**
   * Expande resultados de emails incluyendo los correos del mismo conversationId.
   *
   * Solo lectura: antes asociaba al embarque/OC abierto cada correo del hilo
   * sin asociación, y como Outlook mantiene el conversationId aunque el
   * forwarder cambie de operación en el mismo hilo, cada visita al Buzón
   * pegaba correos de otras importaciones (IM8132 llegó a 1.637). La herencia
   * por hilo la hace el backend al procesar (emailThreadMatcher), con
   * hasMatch y matchSource. Los correos del hilo que ya pertenecen a otro
   * embarque u OC no se muestran aquí.
   */
  const expandByConversationId = async (
    emails: EmailComplete[],
    context?: { module: 'shipment' | 'purchaseOrder'; entityId: string }
  ): Promise<EmailComplete[]> => {
    if (!companyStore.selectedCompanyId || emails.length === 0) return emails

    const seenIds = new Set(emails.map(e => e.id))
    const conversationIds = [...new Set(
      emails.map(e => (e as any).conversationId).filter(Boolean)
    )] as string[]

    if (conversationIds.length === 0) return emails

    const emailsRef = collection(db, 'Companies', companyStore.selectedCompanyId, 'emails')
    const expanded = [...emails]

    // Firestore limita 'in' a 30 valores, agrupar en batches
    const batchSize = 30
    for (let i = 0; i < conversationIds.length; i += batchSize) {
      const batch = conversationIds.slice(i, i + batchSize)
      const convQuery = query(emailsRef, where('conversationId', 'in', batch))
      const convSnapshot = await getDocs(convQuery)

      for (const emailDoc of convSnapshot.docs) {
        if (seenIds.has(emailDoc.id)) continue
        seenIds.add(emailDoc.id)
        if (context && isAssociatedElsewhere(emailDoc.data(), context)) continue
        expanded.push(normalizeEmail(emailDoc))
      }
    }

    // Ordenar por fecha descendente
    expanded.sort((a, b) => toMillis(b.receivedAt) - toMillis(a.receivedAt))

    return expanded
  }

  /**
   * El correo ya está asociado a un embarque/OC distinto del que se está viendo.
   */
  const isAssociatedElsewhere = (
    emailData: any,
    context: { module: 'shipment' | 'purchaseOrder'; entityId: string }
  ): boolean => {
    if (context.module === 'shipment') {
      return !!emailData.matchedShipmentId && emailData.matchedShipmentId !== context.entityId
    }
    const poIds: string[] = Array.isArray(emailData.matchedPurchaseOrderIds)
      ? emailData.matchedPurchaseOrderIds
      : (emailData.matchedPurchaseOrders || []).map((po: any) => po?.id).filter(Boolean)
    return poIds.length > 0 && !poIds.includes(context.entityId)
  }

  /**
   * Obtiene correos relacionados con un embarque específico
   */
  const fetchEmailsByShipment = async (shipmentId: string): Promise<EmailComplete[]> => {
    try {
      isLoading.value = true

      if (!companyStore.selectedCompanyId) {
        console.error('❌ No hay companyId seleccionado')
        return []
      }

      // Buscar correos que tengan matchedShipmentId en la raíz del documento
      const emailsQuery = query(
        collection(db, 'Companies', companyStore.selectedCompanyId, 'emails'),
        where('matchedShipmentId', '==', shipmentId),
        orderBy('receivedAt', 'desc')
      )

      const emailsSnapshot = await getDocs(emailsQuery)

      const results = emailsSnapshot.docs.map(emailDoc => normalizeEmail(emailDoc)).filter(e => !isTrashed(e))

      // Expandir por conversationId: incluir todos los emails del mismo hilo y auto-asociar
      const expandedResults = await expandByConversationId(results, {
        module: 'shipment',
        entityId: shipmentId
      })

      return expandedResults

    } catch (err) {
      console.error('❌ Error obteniendo correos del embarque:', err)
      return []
    } finally {
      isLoading.value = false
    }
  }

  /**
   * Obtiene correos relacionados con un PO específico.
   * Consulta emails donde matchedPurchaseOrders contiene un objeto cuyo id == poId.
   * Firestore no soporta array-contains por sub-campos, así que se filtra client-side.
   */
  const fetchEmailsByPO = async (poId: string): Promise<EmailComplete[]> => {
    try {
      isLoading.value = true

      if (!companyStore.selectedCompanyId) {
        console.error('❌ No hay companyId seleccionado')
        return []
      }

      const emailsRef = collection(db, 'Companies', companyStore.selectedCompanyId, 'emails')
      const seenIds = new Set<string>()
      let results: EmailComplete[] = []

      // 1. Buscar emails asociados a esta OC vía el campo plano matchedPurchaseOrderIds.
      //    Query filtrada en servidor (array-contains) para no descargar toda la colección.
      try {
        const poQuery = query(
          emailsRef,
          where('matchedPurchaseOrderIds', 'array-contains', poId),
          orderBy('receivedAt', 'desc')
        )
        const poSnapshot = await getDocs(poQuery)

        for (const emailDoc of poSnapshot.docs) {
          if (!seenIds.has(emailDoc.id)) {
            seenIds.add(emailDoc.id)
            results.push(normalizeEmail(emailDoc))
          }
        }
      } catch (e) {
        console.warn('⚠️ Error en query matchedPurchaseOrderIds:', e)
      }

      // 2. Fallback: buscar por matchedPoId en raíz (estructura legacy)
      try {
        const rootQuery = query(
          emailsRef,
          where('matchedPoId', '==', poId),
          orderBy('receivedAt', 'desc')
        )
        const rootSnapshot = await getDocs(rootQuery)
        for (const emailDoc of rootSnapshot.docs) {
          if (!seenIds.has(emailDoc.id)) {
            seenIds.add(emailDoc.id)
            results.push(normalizeEmail(emailDoc))
          }
        }
      } catch (e) {
        console.warn('⚠️ Error en query matchedPoId:', e)
      }

      // 3. Fallback: buscar en parsed.references.matchedPoId
      if (results.length === 0) {
        try {
          const nestedQuery = query(
            emailsRef,
            where('parsed.references.matchedPoId', '==', poId),
            orderBy('receivedAt', 'desc')
          )
          const nestedSnapshot = await getDocs(nestedQuery)
          for (const emailDoc of nestedSnapshot.docs) {
            if (!seenIds.has(emailDoc.id)) {
              seenIds.add(emailDoc.id)
              results.push(normalizeEmail(emailDoc))
            }
          }
        } catch (e) {
          console.warn('⚠️ Error en query parsed.references.matchedPoId:', e)
        }
      }

      results = results.filter(e => !isTrashed(e))

      // Expandir por conversationId: incluir todos los emails del mismo hilo y auto-asociar
      results = await expandByConversationId(results, {
        module: 'purchaseOrder',
        entityId: poId
      })

      return results

    } catch (err) {
      console.error('❌ Error obteniendo correos del PO:', err)
      return []
    } finally {
      isLoading.value = false
    }
  }

  /**
   * Recargar correos (sin caché)
   */
  const refreshEmails = async (filters: EmailFilters = {}) => {
    await fetchEmails(filters, true)
  }

  /**
   * Configurar listener en tiempo real para nuevos correos
   */
  const setupRealtimeListener = (shipmentId?: string) => {
    if (!companyStore.selectedCompanyId) return

    const emailAuthStore = useEmailAuthStore()
    const userEmail = emailAuthStore.user?.user.email
    if (!DEBUG_SKIP_EMAIL_AUTH && !userEmail) return

    const emailsRef = collection(db, 'Companies', companyStore.selectedCompanyId, 'emails')

    // Para shipmentId, no necesitamos dual query — filtra por embarque
    if (shipmentId) {
      const emailsQuery = query(
        emailsRef,
        where('parsed.references.matchedShipmentId', '==', shipmentId),
        orderBy('receivedAt', 'desc')
      )

      return onSnapshot(emailsQuery, (snapshot) => {
        totalItems.value = snapshot.size
        const updatedEmails = snapshot.docs.map(emailDoc => normalizeEmail(emailDoc))
        emails.value = updatedEmails
      }, (err) => {
        console.error('❌ Error en listener de correos:', err)
        error.value = err.message
      })
    }

    // Sin shipmentId: dual listener syncedFromEmail + toAddresses
    if (DEBUG_SKIP_EMAIL_AUTH || !userEmail) {
      const emailsQuery = query(emailsRef, orderBy('receivedAt', 'desc'), limit(100))
      return onSnapshot(emailsQuery, (snapshot) => {
        totalItems.value = snapshot.size
        emails.value = snapshot.docs.map(emailDoc => normalizeEmail(emailDoc))
      }, (err) => {
        console.error('❌ Error en listener de correos:', err)
        error.value = err.message
      })
    }

    const syncedResults = new Map<string, any>()
    const toResults = new Map<string, any>()

    const mergeAndUpdate = () => {
      const merged = new Map([...syncedResults, ...toResults])
      const sorted = [...merged.values()]
        .sort((a, b) => {
          const timeA = a.receivedAt?.toMillis?.() ?? 0
          const timeB = b.receivedAt?.toMillis?.() ?? 0
          return timeB - timeA
        })
        .slice(0, 50)
      totalItems.value = sorted.length
      emails.value = sorted.map(data => normalizeEmail({ id: data.id, data: () => data }))
    }

    const unsub1 = onSnapshot(
      query(emailsRef, where('syncedFromEmail', '==', userEmail), orderBy('receivedAt', 'desc'), limit(100)),
      (snapshot) => {
        syncedResults.clear()
        snapshot.docs.forEach(d => syncedResults.set(d.id, { ...d.data(), id: d.id }))
        mergeAndUpdate()
      },
      (err) => { console.error('❌ Error listener syncedFromEmail:', err); error.value = err.message }
    )

    const unsub2 = onSnapshot(
      query(emailsRef, where('toAddresses', 'array-contains', userEmail), orderBy('receivedAt', 'desc'), limit(100)),
      (snapshot) => {
        toResults.clear()
        snapshot.docs.forEach(d => toResults.set(d.id, { ...d.data(), id: d.id }))
        mergeAndUpdate()
      },
      (err) => { console.error('❌ Error listener toAddresses:', err); error.value = err.message }
    )

    // Retornar función que limpia ambos listeners
    return () => { unsub1(); unsub2() }
  }


  /**
   * Ejecuta una acción sugerida de un correo
   */
  const executeEmailAction = async (
    emailId: string,
    actionId: string
  ) => {
    try {
      const executeAction = httpsCallable(functions, 'executeEmailActions')
      
      const result = await executeAction({
        emailId,
        actionId,
        companyId: companyStore.selectedCompanyId,
      })

      return result.data
    } catch (err: any) {
      console.error('❌ Error ejecutando acción:', err)
      throw err
    }
  }

  /**
   * Cambiar página
   */
  const setPage = (page: number) => {
    currentPage.value = page
  }

  const searchRepository = new EmailSearchRepository()
  const searchPageSize = 50
  const isLoadingMoreSearch = ref(false)
  const searchFilters = ref<Omit<EmailSearchRequest, 'params'> | null>(null)
  const searchPage = ref(1)
  // Cuenta los hits que devolvió BigQuery, no los correos hidratados: un id que ya
  // no exista en Firestore no vuelve, y comparar contra `emails.length` dejaría el
  // botón "cargar más" encendido para siempre.
  const searchHitsLoaded = ref(0)
  let searchAbortController: AbortController | null = null

  const hasMoreSearchResults = computed(() => searchHitsLoaded.value < totalItems.value)

  /**
   * Trae de Firestore los documentos que BigQuery marcó como coincidentes.
   *
   * Los pide en lotes de 30 por el límite del operador 'in', y no ordena en la
   * query: el orden lo define BigQuery y se reconstruye acá. Un id que ya no
   * exista en Firestore (correo borrado después de replicarse) simplemente no
   * vuelve, así que `emails.length` puede ser menor que `totalItems`.
   * Un correo en papelera tampoco vuelve: desde KILA-367 el borrado es un soft
   * delete y BigQuery lo sigue devolviendo (KILA-463).
   */
  const hydrateEmailsByIds = async (companyId: string, ids: string[]): Promise<EmailComplete[]> => {
    if (ids.length === 0) return []

    const emailsRef = collection(db, 'Companies', companyId, 'emails')
    const chunks: string[][] = []
    for (let i = 0; i < ids.length; i += 30) chunks.push(ids.slice(i, i + 30))

    const snapshots = await Promise.all(
      chunks.map(chunk => getDocs(query(emailsRef, where(documentId(), 'in', chunk))))
    )

    const firestoreEmailStore = useFirestoreEmailStore()
    const byId = new Map<string, EmailComplete>()
    for (const snapshot of snapshots) {
      for (const emailDoc of snapshot.docs) {
        firestoreEmailStore.setRawEmailData(emailDoc.id, { ...emailDoc.data(), id: emailDoc.id })
        byId.set(emailDoc.id, normalizeEmail(emailDoc))
      }
    }

    return ids
      .map(id => byId.get(id))
      .filter((email): email is EmailComplete => !!email && !isTrashed(email))
  }

  const runSearch = async (page: number, append: boolean) => {
    const companyId = companyStore.selectedCompanyId
    if (!companyId) {
      error.value = 'No hay compañía seleccionada'
      return
    }

    // Cancelar la búsqueda anterior: sin esto, al teclear rápido una respuesta
    // vieja puede llegar después de la nueva y pisar los resultados.
    searchAbortController?.abort()
    const controller = new AbortController()
    searchAbortController = controller

    if (append) isLoadingMoreSearch.value = true
    else isLoading.value = true
    error.value = null

    try {
      // Correo de la cuenta conectada por OAuth, no el de Firebase Auth: es con el
      // que el inbox filtra sus listeners y con el que se guardan los correos.
      //
      // Se prueban las dos rutas porque la sesión se puebla por dos caminos y el
      // correo del buzón queda anidado en `user.user` o en la raíz segun cual fue:
      // firestoreEmail.ts lee la anidada y el store expone la de la raíz.
      const authUser = useEmailAuthStore().user as any
      const mailbox = authUser?.user?.email || authUser?.email || ''

      // Con DEBUG_SKIP_EMAIL_AUTH el inbox muestra los correos de toda la compañía y
      // no hay cuenta OAuth conectada, asi que la busqueda pide el mismo alcance para
      // no quedar desalineada con lo que se ve en pantalla.
      if (!mailbox && !DEBUG_SKIP_EMAIL_AUTH) {
        error.value = 'No hay una cuenta de correo conectada para buscar'
        return
      }

      const response = await searchRepository.search(
        {
          ...(searchFilters.value || {}),
          params: { company: companyId },
          ...(mailbox ? { mailbox } : { scope: 'company' as const }),
          page,
          pageSize: searchPageSize
        },
        controller.signal
      )

      const hydrated = await hydrateEmailsByIds(companyId, response.data.map(hit => hit.emailId))
      if (controller.signal.aborted) return

      emails.value = append ? [...emails.value, ...hydrated] : hydrated
      totalItems.value = response.metadata.total
      searchHitsLoaded.value = append
        ? searchHitsLoaded.value + response.data.length
        : response.data.length
      searchPage.value = response.metadata.page
    } catch (err: any) {
      if (err?.name === 'AbortError') return
      error.value = err.message || 'Error al buscar correos'
      if (!append) {
        emails.value = []
        totalItems.value = 0
        searchHitsLoaded.value = 0
      }
    } finally {
      if (controller === searchAbortController) {
        if (append) isLoadingMoreSearch.value = false
        else isLoading.value = false
      }
    }
  }

  /**
   * Busca correos vía BigQuery (endpoint SEARCH_EMAILS) e hidrata los resultados
   * desde Firestore.
   *
   * Reemplaza el enfoque anterior —bajar los 500 correos más recientes de la
   * compañía y filtrarlos en memoria— que dejaba fuera todo lo más viejo, no
   * acotaba por usuario y solo acertaba substrings contiguos.
   */
  const searchEmails = async (filters: Omit<EmailSearchRequest, 'params'>) => {
    const hasQuery = !!filters.query?.trim()
    const hasOtherFilter = Object.entries(filters).some(
      ([key, value]) => key !== 'query' && value !== undefined && value !== '' && value !== null
    )
    if (!hasQuery && !hasOtherFilter) {
      clearSearch()
      return
    }

    searchFilters.value = filters
    await runSearch(1, false)
  }

  /** Siguiente página de resultados; concatena en vez de reemplazar. */
  const loadMoreSearchResults = async () => {
    if (!searchFilters.value || isLoadingMoreSearch.value || !hasMoreSearchResults.value) return
    await runSearch(searchPage.value + 1, true)
  }

  /**
   * Limpiar búsqueda
   */
  const clearSearch = () => {
    searchAbortController?.abort()
    searchAbortController = null
    searchFilters.value = null
    searchPage.value = 1
    searchHitsLoaded.value = 0
    emails.value = []
    error.value = null
    totalItems.value = 0
  }

  /**
   * Id y remitente del correo de la compañía con ese Message-ID, o null.
   * Sirve para responder a un correo que llegó como adjunto sobre su hilo
   * real, si el mismo mensaje también entró a algún buzón de la compañía.
   * Devuelve el remitente para que el llamador pueda confirmar que es el
   * mismo correo (ver `useEmbeddedEmailReply.ts`): el Message-ID lo controla
   * un tercero, y una coincidencia casual no debería abrir el hilo de otro.
   *
   * `limit(2)` basta en casi todos los casos (los duplicados conocidos de
   * Message-ID en una compañía son de a dos); el documento pesa cerca de un
   * MiB y el SDK web no tiene proyección, así que no conviene bajar más de
   * la cuenta solo para leer `id` y `folder`.
   */
  const findEmailIdByInternetMessageId = async (messageId: string): Promise<{ id: string; fromAddress: string } | null> => {
    const companyId = companyStore.selectedCompanyId
    if (!companyId || !messageId) return null

    const bare = messageId.trim().replace(/^<|>$/g, '')
    if (!bare) return null

    const emailsRef = collection(db, 'Companies', companyId, 'emails')
    const snapshot = await getDocs(
      query(emailsRef, where('internetMessageId', 'in', [`<${bare}>`, bare]), limit(2))
    )

    // Misma regla de papelera que el resto del buzón (resolveUserFolder): es
    // compartida y manda sobre folderByUser, así que ni hace falta el id de
    // la conexión de quien pregunta.
    const match = snapshot.docs.find(d => resolveUserFolder(d.data() as FolderDocFields) !== 'trash')
    if (!match) return null

    const data = match.data() as any
    return { id: match.id, fromAddress: String(data.fromAddress || '') }
  }

  // ============================================================================
  // RETURN
  // ============================================================================

  return {
    // Estado
    emails,
    isLoading,
    error,
    totalItems,
    currentPage,
    pageSize,
    totalPages,
    // Computed
    matchedEmails,
    pendingEmails,
    processedEmails,
    // Métodos
    fetchEmails,
    fetchEmailById,
    fetchEmailsByShipment,
    fetchEmailsByPO,
    findEmailIdByInternetMessageId,
    refreshEmails,
    setupRealtimeListener,
    executeEmailAction,
    setPage,
    searchEmails,
    loadMoreSearchResults,
    clearSearch,
    // Búsqueda
    isLoadingMoreSearch,
    hasMoreSearchResults
  }
}
